import { type ReactNode, useMemo } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { theme } from '@styles/theme';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

interface ScrollRevealProps {
  children: ReactNode;
  direction?: Direction;
  distance?: number;
  delay?: number;
  duration?: number;
  threshold?: number;
  once?: boolean;
  className?: string;
  as?: keyof HTMLElementTagNameMap;
}

function getHidden(direction: Direction, distance: number) {
  switch (direction) {
    case 'up':    return { opacity: 0, y: distance };
    case 'down':  return { opacity: 0, y: -distance };
    case 'left':  return { opacity: 0, x: distance };
    case 'right': return { opacity: 0, x: -distance };
    case 'none':  return { opacity: 0 };
  }
}

export function ScrollReveal({
  children,
  direction = 'up',
  distance = 40,
  delay = 0,
  duration = theme.durations.slower,
  threshold = 0.15,
  once = true,
  className,
  as = 'div',
}: ScrollRevealProps) {
  const shouldReduceMotion = useReducedMotion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const MotionTag = useMemo(() => motion(as), [as]);

  return (
    <MotionTag
      className={className}
      initial={shouldReduceMotion ? { opacity: 0 } : getHidden(direction, distance)}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, amount: threshold }}
      transition={{
        duration: shouldReduceMotion ? theme.durations.fast : duration,
        delay,
        ease: theme.easings.smoothOut,
      }}
    >
      {children}
    </MotionTag>
  );
}
